import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Search, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ToyRequestField from "@/components/ToyRequestField";
import useToySuggestions from "@/hooks/useToySuggestions";
import ReportTableScroll from "@/components/ReportTableScroll";
import "./ToyRequestReview.css";

const TH = "text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs";
const muted = { color: "var(--fb-text-muted)" };
const border = { borderColor: "var(--fb-border)" };
const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];

const validItems = (t) => Array.isArray(t?.toy_items) && t.toy_items.every((x) => typeof x === "string");
const editableItems = (t) => t?.toy_items == null || validItems(t);
const itemsOf = (t) => (validItems(t) ? t.toy_items : []);
const sameItems = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);
const rowKey = (t) => `${t.household_record_id}:${t.member_record_id}`;

export function matchesToySearch(t, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  const hay = [t.name, t.household_id, t.member_id, ...itemsOf(t)];
  return hay.some((v) => v && String(v).toLowerCase().includes(q));
}

function RowEditor({ row, year, canEditGender, onSaved, onReload, onClose, onDirtyChange, suggestions }) {
  const [fieldKey, setFieldKey] = useState(0);
  const [reloading, setReloading] = useState(false);
  const [current, setCurrent] = useState(null);
  const [rebased, setRebased] = useState(null);
  const reloadSeq = useRef(0);
  const [draft, setDraft] = useState(itemsOf(row));
  const [gender, setGender] = useState(row.gender || "");
  const [base, setBase] = useState({ items: itemsOf(row), gender: row.gender || "", token: row.toy_request_token });
  const [pending, setPending] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const changed = !sameItems(draft, base.items) || (canEditGender && gender !== base.gender);
  const dirty = saving || changed || !!pending.trim();
  useEffect(() => { onDirtyChange(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onDirtyChange(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Quiet rebase when another session changed the row and we have no edits.
  useEffect(() => {
    if (!changed && !pending.trim() && !saving && row.toy_request_token !== base.token) {
      setBase({ items: itemsOf(row), gender: row.gender || "", token: row.toy_request_token });
      setDraft(itemsOf(row));
      setGender(row.gender || "");
    }
  }, [row.toy_request_token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    if (saving) return;
    if (pending.trim()) { setError("Add or clear the typed item before saving."); return; }
    setSaving(true); setError("");
    const body = { year, toy_items: draft, expected_token: base.token,
      ...(canEditGender && gender !== base.gender ? { gender } : {}) };
    try {
      const { data } = await api.patch(
        `/sharing-tree/households/${row.household_record_id}/members/${row.member_record_id}/toy-request`, body);
      if (!alive.current) return;
      onSaved(year, row, data);
      onClose();
    } catch (e) {
      if (!alive.current) return;
      setSaving(false);
      if (e?.response?.status === 409) setConflict(true);
      else setError(formatApiError(e));
    }
  }

  async function reload() {
    const seq = ++reloadSeq.current;
    setError(""); setReloading(true);
    const fresh = await onReload(row);
    if (!alive.current || seq !== reloadSeq.current) return;
    setReloading(false);
    if (!fresh) { setError("Could not reload the current request. Your draft is kept."); return; }
    setBase({ items: itemsOf(fresh), gender: fresh.gender || "", token: fresh.toy_request_token });
    setCurrent({ items: itemsOf(fresh), gender: fresh.gender || "" });
  }

  function useCurrent() {
    setDraft(current.items); setGender(current.gender); setFieldKey((k) => k + 1); setPending("");
    setConflict(false); setCurrent(null); setRebased(null);
  }

  function keepDraft() {
    setRebased(current); setConflict(false); setCurrent(null);
  }

  return (
    <div className="fb-toy-row-editor max-w-[calc(100vw-6rem)] sm:max-w-3xl space-y-3" data-testid={`toy-editor-${row.member_record_id}`}>
      {canEditGender && <label className="block text-sm font-medium">
        Gender
        <select value={gender} onChange={e => setGender(e.target.value)} disabled={saving}
                data-testid="toy-edit-gender"
                className="block mt-1 w-full sm:max-w-xs px-3 py-2 rounded-lg border bg-white" style={border}>
          <option value="">Not specified</option>
          {gender && !GENDERS.includes(gender) && <option value={gender}>{gender}</option>}
          {GENDERS.map(value => <option key={value} value={value}>{value}</option>)}
        </select>
      </label>}
      <ToyRequestField
        key={fieldKey}
        value={draft} onChange={setDraft} suggestions={suggestions.items}
        suggestionsLoading={suggestions.loading} suggestionsError={suggestions.error}
        onRetrySuggestions={suggestions.retry} testIdPrefix={`toy-edit-${row.member_record_id}`}
        label={`Toy items for ${row.name || row.member_id}`}
        onPendingTextChange={setPending}
      />
      {rebased && (
        <p className="text-xs rounded-lg p-2 fb-status-warning" data-testid="toy-edit-rebased">
          Saving will replace the current request ({rebased.items.length ? rebased.items.join(", ") : "no items"}{canEditGender && `; Gender: ${rebased.gender || "not specified"}`}) with your draft.
        </p>
      )}
      {conflict && (
        <div role="alert" className="text-sm rounded-lg p-3 space-y-2 fb-status-warning" data-testid="toy-edit-conflict">
          <div>Another session changed this toy request while you were editing. Your draft is kept.</div>
          {current === null ? (
            <button type="button" data-testid="toy-edit-reload" disabled={reloading} onClick={reload}
                    className="px-3 py-1.5 rounded-lg border text-sm font-semibold bg-white disabled:opacity-50" style={border}>
              {reloading ? "Reloading…" : "Reload current request"}
            </button>
          ) : (
            <>
              <div data-testid="toy-edit-current">Current request: {current.items.length ? current.items.join(", ") : "no items"}{canEditGender && `; Gender: ${current.gender || "not specified"}`}</div>
              <div className="flex gap-2 flex-wrap">
                <button type="button" data-testid="toy-edit-reload-discard" onClick={useCurrent}
                        className="px-3 py-1.5 rounded-lg border text-sm font-semibold bg-white" style={border}>
                  Use current request (discard my draft)
                </button>
                <button type="button" data-testid="toy-edit-reload-keep" onClick={keepDraft}
                        className="px-3 py-1.5 rounded-lg border text-sm font-semibold bg-white" style={border}>
                  Keep my draft and overwrite on next save
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {error && <p role="alert" className="text-xs font-medium" style={{ color: "var(--fb-error)" }} data-testid="toy-edit-error">{error}</p>}
      <div className="flex gap-2">
        <button type="button" data-testid="toy-edit-save" onClick={save} disabled={saving || conflict || reloading || !changed && !pending.trim()}
                className="fb-btn-primary px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" data-testid="toy-edit-cancel" onClick={onClose} disabled={saving}
                className="px-4 py-2 rounded-lg border text-sm font-semibold disabled:opacity-50" style={border}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function ToyRequestReview({
  year, rows, error, canEdit, canEditGender = true, readOnlyReason, guardRef, onSaved, onReload, onRowReloaded, printButton, exportControl,
}) {
  const [query, setQuery] = useState("");
  const [editingKey, setEditingKey] = useState(null);
  const [warning, setWarning] = useState("");
  const dirtyRef = useRef(false);
  const tableScrollRef = useRef(null);
  const editorRowRef = useRef(null);
  const suggestions = useToySuggestions();
  const editing = editingKey !== null;
  useEffect(() => {
    if (editingKey && tableScrollRef.current) {
      const scroller = tableScrollRef.current;
      scroller.scrollLeft = 0;
      // Reveal the editor below the sticky header, even for the last request.
      if (editorRowRef.current) {
        scroller.scrollTop += editorRowRef.current.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top - scroller.querySelector("thead").getBoundingClientRect().height;
      }
    }
  }, [editingKey]);

  function setDirty(v) { dirtyRef.current = v; if (!v) setWarning(""); }

  guardRef.current = {
    isDirty: () => dirtyRef.current,
    check: () => {
      if (!dirtyRef.current) return true;
      setWarning("You have unsaved toy request changes. Save or Cancel the editor before printing or exporting.");
      return false;
    },
  };
  useEffect(() => () => { guardRef.current = { isDirty: () => false, check: () => true }; }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function beforeUnload(e) { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; } }
    function onFocus() { if (!dirtyRef.current && !editing) onReload(null); }
    window.addEventListener("beforeunload", beforeUnload);
    window.addEventListener("focus", onFocus);
    return () => { window.removeEventListener("beforeunload", beforeUnload); window.removeEventListener("focus", onFocus); };
  }, [editing, onReload]);

  const list = rows || [];
  const filtered = useMemo(() => list.filter((t) => matchesToySearch(t, query) || rowKey(t) === editingKey), [list, query, editingKey]);
  const editRow = editingKey ? list.find((t) => rowKey(t) === editingKey) : null;

  useEffect(() => { if (editingKey && rows && !editRow) setEditingKey(null); }, [editingKey, rows, editRow]);

  async function reloadRow(row) {
    const r = await onReload(row);
    if (!r) return null;
    const fresh = (r.toy_requests || []).find((t) => rowKey(t) === rowKey(row)) || null;
    if (fresh && editableItems(fresh)) { onRowReloaded?.(fresh); return fresh; }
    return null;
  }

  return (
    <div className="fb-card p-4 sm:p-6" data-testid="toy-request-review">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Toy Request List (≤9)</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {printButton(() => guardRef.current.check())}
          {exportControl}
        </div>
      </div>
      {warning && (
        <div role="alert" className="flex items-start gap-2 text-sm rounded-lg px-3 py-2 mb-3 fb-status-warning" data-testid="toy-unsaved-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {warning}
        </div>
      )}
      {readOnlyReason && <p className="text-xs mb-3" style={muted} data-testid="toy-readonly-note">{readOnlyReason}</p>}
      {error && !rows && <p role="alert" className="text-sm" data-testid="toy-report-error">{error}</p>}
      {!rows && !error && <div role="status" className="py-6 text-sm text-center" style={muted}>Loading toy requests…</div>}
      {rows && list.length === 0 && (
        <div className="py-8 text-center text-sm" style={muted}>No toy requests recorded for {year}.</div>
      )}
      {rows && list.length > 0 && (
        <>
          <div className="relative mb-3 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={muted} />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                   data-testid="toy-search" aria-label="Search toy requests"
                   placeholder="Search name, ID or toy item"
                   className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm" style={border} />
          </div>
          {editing && query.trim() && (
            <p className="text-xs mb-2" style={muted} data-testid="toy-search-pinned">The row being edited stays visible while you search.</p>
          )}
          {filtered.length === 0 && (
            <div className="py-6 text-center text-sm" style={muted} data-testid="toy-search-empty">No children match “{query.trim()}”.</div>
          )}
          {filtered.length > 0 && (
            <ReportTableScroll ref={tableScrollRef} label="Toy Request List (≤9) table">
              <table className="w-full min-w-[640px] text-sm">
                <thead><tr style={{ background: "var(--fb-bg)" }}>
                  {["Household ID", "Member ID", "Name", "Age", "Gender", "Gift Request"].map((h) => <th key={h} className={TH} style={muted}>{h}</th>)}
                  {canEdit && <th className={TH} style={muted}><span className="sr-only">Actions</span></th>}
                </tr></thead>
                <tbody>
                  {filtered.map((t) => {
                    const key = rowKey(t);
                    const isEditing = key === editingKey;
                    const items = itemsOf(t);
                    const editable = canEdit && editableItems(t) && t.household_record_id && t.member_record_id && t.toy_request_token;
                    return (
                      <React.Fragment key={key + ":" + t.member_id}>
                        <tr className="border-t align-top" style={border} data-testid={`toy-row-${t.member_id}`}>
                          <td className="px-4 py-3 font-mono text-xs">{t.household_id}</td>
                          <td className="px-4 py-3 font-mono text-xs">{t.member_id}</td>
                          <td className="px-4 py-3 font-medium">{t.name || "—"}</td>
                          <td className="px-4 py-3 tabular-nums">{t.age}</td>
                          <td className="px-4 py-3">{t.gender || "—"}</td>
                          <td className="px-4 py-3" style={{ overflowWrap: "anywhere" }} data-testid={`toy-items-${t.member_id}`}>
                            {items.length > 0 ? (
                              <ul className="flex flex-wrap gap-1">
                                {items.map((i) => <li key={i} className="px-2 py-0.5 rounded-full text-xs border" style={border}>{i}</li>)}
                              </ul>
                            ) : <span style={muted}>No toy items requested</span>}
                          </td>
                          {canEdit && (
                            <td className="px-4 py-3">
                              {editable && (
                                <button type="button" data-testid={`toy-edit-${t.member_id}`}
                                        disabled={editing && !isEditing}
                                        aria-label={`Edit toy items for ${t.name || t.member_id}`}
                                        onClick={() => setEditingKey(key)}
                                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border hover:bg-stone-50 disabled:opacity-50" style={border}>
                                  <Pencil className="w-3.5 h-3.5" /> Edit
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                        {isEditing && editRow && (
                          <tr ref={editorRowRef} style={{ background: "var(--fb-bg)" }}>
                            <td colSpan={canEdit ? 7 : 6} className="px-4 py-3">
                              <RowEditor key={key} row={editRow} year={year} suggestions={suggestions} canEditGender={canEditGender}
                                         onSaved={onSaved} onReload={reloadRow}
                                         onDirtyChange={setDirty} onClose={() => { setDirty(false); setEditingKey(null); }} />
                            </td>
                          </tr>
                        )}
                        {(t.toy_notes || t.legacy_gift_request) && (
                          <tr data-testid={`toy-staff-notes-${t.member_id}`} style={{ background: "var(--fb-bg)" }}>
                            <td colSpan={canEdit ? 7 : 6} className="px-4 py-2 text-xs space-y-0.5">
                              {t.toy_notes && <div><span className="font-semibold">Staff-only toy notes:</span> {t.toy_notes}</div>}
                              {t.legacy_gift_request && <div><span className="font-semibold">Original request (staff review, not on labels):</span> {t.legacy_gift_request}</div>}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </ReportTableScroll>
          )}
        </>
      )}
    </div>
  );
}
